<script setup lang="ts">
import { ButtonQC } from '@qicgroupdoha/qic-connect-ds';
import { computed, defineAsyncComponent, inject, onMounted, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { usePolicyStore } from '@/entities/Policy';
import { GA_EVENTS, useGoogleAnalytics } from '@/shared/analytics';
import { FlowCard } from '@/shared/ui/FlowCard';
import { ContactUsWidget } from '@/widgets/ContactUsWidget';

const { error } = defineProps<{ error: any }>();
const emit = defineEmits<ExceptionWidgetEmit>();
const ReloadIcon = defineAsyncComponent(() => import('@/shared/ui/icons/ReloadIcon.vue'));

interface ExceptionWidgetEmit {
  (e: 'resolve'): void;
}

const landingUrl = inject<string>('landingUrl') ?? '/';

const { t } = useI18n();

const title = t('qic.error.title');
const text = t('qic.error.desc');

const gaService = useGoogleAnalytics();
const route = useRoute();
const policyStore = usePolicyStore();

const errorMessage = computed(() => `${error?.originalError?.source} - ${error?.originalError?.sentryErrorId}`);

function getErrorDetails() {
  const rawError = toRaw(error);
  console.error(rawError);
}

onMounted(() => {
  gaService.send(GA_EVENTS.ERROR_PAGE, {
    prev_page: `${window.location.origin}/${route.fullPath}`,
    title,
    text,
    policy_id: policyStore.state.policyId,
  });
});
</script>

<template>
  <FlowCard controls-off>
    <div class="rythm-wrap-section-list">
      <div class="something-wrong__message">
        <h2 class="something-wrong__title">
          {{ title }}
        </h2>
        <p class="something-wrong__desc">
          {{ text }}
        </p>
        <p class="something-wrong__error">
          <button
            data-test="showe-error-btn"
            class="something-wrong__error-btn"
            @click="getErrorDetails"
          >
            {{ errorMessage }}
          </button>
        </p>
      </div>

      <ButtonQC class="something-wrong__reload-btn" size="large" @click="emit('resolve')">
        {{ $t('qic.error.tryagain') }}
        <template #icon-after-content>
          <ReloadIcon />
        </template>
      </ButtonQC>

      <div class="rythm-wrap--end">
        <a :href="landingUrl" class="something-wrong__home-link" data-test="landing-link">
          {{ $t('qic.error.home') }}
        </a>
      </div>
    </div>

    <ContactUsWidget :title="title" :text="text" />
  </FlowCard>
</template>

<style lang="scss" scoped>
@use 'ErrorWidget';
</style>
